/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import assign from 'lodash/assign';
import get from 'lodash/get';
import { performance } from '@jira/platform__observability-performance-timer/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createSubscriber,
	createContainer,
	createHook,
	type Action,
	type BoundActions,
	type HookFunction,
} from '@atlassian/react-sweet-state';
import {
	type FormattedRenderTraceData,
	type FormattedTraces,
	type RenderTrace,
	type RenderTracerContainerProps,
	type RenderTracerMarkProps,
	type State,
	SSR_STATE_KEY_DETAIL_RENDER_TRACE,
} from './types.tsx';

const initialState: State = { start: undefined, traces: {} };

let currentTraces: RenderTrace = {};

export const getRenderTrace = (): RenderTrace => currentTraces;

export const traceStart = (key: string): void => {
	if (currentTraces[key]) return;
	currentTraces[key] = { startTime: performance.now() };
};

export const traceEnd = (key: string): void => {
	if (!currentTraces[key] || currentTraces[key].duration != null) return;
	currentTraces[key].duration = performance.now() - currentTraces[key].startTime;
};

const actions = {
	startTrace:
		(name: string, time: number): Action<State, RenderTracerContainerProps> =>
		({ setState, getState }, { startKey }) => {
			const { start, traces } = getState();
			// If <RenderTracerContainer /> has not been setup with a startKey and startTime, return
			if (start === undefined) return;

			const key = name === startKey ? name : `${startKey}/${name}`;

			if (traces[key]) return;

			setState({
				traces: assign({}, traces, {
					[key]: {
						startTime: time,
					},
				}),
			});
		},

	endTrace:
		(name: string, time: number): Action<State, RenderTracerContainerProps> =>
		({ setState, getState }, { startKey }) => {
			const { start, traces } = getState();

			if (start === undefined) return;

			const key = name === startKey ? name : `${startKey}/${name}`;

			const trace = traces[key] || {
				startTime: 0,
			};

			setState({
				traces: assign({}, traces, {
					[key]: {
						...trace,
						duration: time - trace.startTime,
					},
				}),
			});

			currentTraces = Object.assign(currentTraces, getState().traces);
		},
} as const;

type Actions = typeof actions;

const Store = createStore({
	name: 'jira-render-tracer',
	initialState,
	actions,
});

const RenderTracerActions = createSubscriber<State, Actions, undefined, undefined>(Store, {
	selector: null,
});

export const useRenderTracerActions: HookFunction<
	State,
	BoundActions<
		State,
		{
			readonly startTrace: (
				name: string,
				time: number,
			) => Action<State, RenderTracerContainerProps>;
			readonly endTrace: (name: string, time: number) => Action<State, RenderTracerContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: null,
});

const RenderTracerContainerBase = createContainer<State, Actions, RenderTracerContainerProps>(
	Store,
	{
		onInit:
			() =>
			({ setState }, { startKey, startTime }) => {
				setState({
					start: startTime,
					traces: {
						[startKey]: {
							startTime: performance.now(),
						},
					},
				});
			},
	},
);

export const RenderTracerContainer = ({
	children,
	startKey,
	startTime,
}: RenderTracerContainerProps): React.JSX.Element => (
	<RenderTracerContainerBase startKey={startKey} startTime={startTime}>
		{children}
		<RenderTracerMark traceKey={startKey} type="end" />
	</RenderTracerContainerBase>
);

export const RenderTracerMark = ({ traceKey, type }: RenderTracerMarkProps): React.JSX.Element => (
	// @ts-expect-error - TS2745 - This JSX tag's 'children' prop expects type 'never' which requires multiple children, but only a single child was provided.
	<RenderTracerActions>
		{/* @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7031 - Binding element 'startTrace' implicitly has an 'any' type. | TS7031 - Binding element 'endTrace' implicitly has an 'any' type. */}
		{(_, { startTrace, endTrace }) => {
			if (type === 'start') {
				startTrace(traceKey, performance.now());
			} else if (type === 'end') {
				endTrace(traceKey, performance.now());
			}

			return null;
		}}
	</RenderTracerActions>
);

export const formatSSRTraces = (
	appName: string | null = 'unknown',
	traces: RenderTrace = currentTraces,
): FormattedRenderTraceData => ({
	[SSR_STATE_KEY_DETAIL_RENDER_TRACE]: {
		data: {
			traces,
			routeName: appName,
		},
	},
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getRenderTraceFromSSRGlobal = (): FormattedTraces | Record<any, any> =>
	get(
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.SPA_STATE || window.SKELETON_NAVIGATION || {},
		[SSR_STATE_KEY_DETAIL_RENDER_TRACE, 'data'],
		{},
	);
